/**
 * Site-mode detection for the windier.app / forecast.windier.app split.
 *
 * Both subdomains are served by the exact same Vercel deployment — one
 * build, no separate bundles — so the app decides what to render purely
 * at startup, based on which hostname it's running under:
 *   - windier.app            -> marketing site (home + /contact)
 *   - forecast.windier.app   -> the actual weather app, gated by sign-in
 *
 * Local dev has no real subdomains to key off, so `?mode=forecast` (or
 * `?mode=marketing`) overrides detection — e.g.
 * http://localhost:3000/?mode=forecast to work on the app locally, since
 * the default without the param is 'marketing'.
 */

const MARKETING_HOST = 'windier.app';
const FORECAST_HOST = 'forecast.windier.app';

const isKnownProdHost = (hostname) =>
  hostname === MARKETING_HOST || hostname === FORECAST_HOST;

export const getSiteMode = () => {
  if (typeof window === 'undefined') return 'marketing';

  const override = new URLSearchParams(window.location.search).get('mode');
  if (override === 'forecast' || override === 'marketing') return override;

  const hostname = window.location.hostname;
  if (hostname === FORECAST_HOST || hostname.startsWith('forecast.')) {
    return 'forecast';
  }
  return 'marketing';
};

/**
 * Absolute URL to the forecast app's home — used by the marketing site's
 * Login/View Forecasts buttons to navigate across subdomains (a real
 * cross-origin nav, not client-side routing: sign-in only happens on
 * forecast.windier.app, since that's the origin whose session actually
 * needs to persist).
 */
export const getForecastUrl = () => {
  if (typeof window !== 'undefined' && !isKnownProdHost(window.location.hostname)) {
    return `${window.location.origin}/?mode=forecast`;
  }
  return `https://${FORECAST_HOST}/`;
};

/** Absolute URL to the marketing homepage. */
export const getMarketingUrl = () => {
  if (typeof window !== 'undefined' && !isKnownProdHost(window.location.hostname)) {
    return `${window.location.origin}/?mode=marketing`;
  }
  return `https://${MARKETING_HOST}/`;
};

/** Absolute URL to the marketing site's contact page. */
export const getContactUrl = () => {
  if (typeof window !== 'undefined' && !isKnownProdHost(window.location.hostname)) {
    return `${window.location.origin}/contact?mode=marketing`;
  }
  return `https://${MARKETING_HOST}/contact`;
};
